import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  User, Shirt, Glasses, Smile, Palette, Sparkles, 
  RotateCcw, Check
} from "lucide-react";
import { toast } from "sonner";

// DiceBear v7.x Avataaars options - using correct API values
const AVATAR_OPTIONS = {
  // Top/Hair styles - v7.x values
  top: [
    { value: "shortFlat", label: "Short Flat" },
    { value: "shortWaved", label: "Short Waved" },
    { value: "shortCurly", label: "Short Curly" },
    { value: "shortRound", label: "Short Round" },
    { value: "dreads01", label: "Dreads 1" },
    { value: "dreads02", label: "Dreads 2" },
    { value: "frizzle", label: "Frizzle" },
    { value: "shaggy", label: "Shaggy" },
    { value: "shaggyMullet", label: "Shaggy Mullet" },
    { value: "theCaesar", label: "Caesar" },
    { value: "theCaesarAndSidePart", label: "Caesar Side Part" },
    { value: "bigHair", label: "Big Hair" },
    { value: "bob", label: "Bob" },
    { value: "bun", label: "Bun" },
    { value: "curly", label: "Long Curly" },
    { value: "curvy", label: "Long Curvy" },
    { value: "dreads", label: "Long Dreads" },
    { value: "frida", label: "Frida" },
    { value: "fro", label: "Fro" },
    { value: "froBand", label: "Fro Band" },
    { value: "miaWallace", label: "Mia Wallace" },
    { value: "longButNotTooLong", label: "Not Too Long" },
    { value: "shavedSides", label: "Shaved Sides" },
    { value: "straight01", label: "Straight 1" },
    { value: "straight02", label: "Straight 2" },
    { value: "straightAndStrand", label: "Straight Strand" },
    { value: "sides", label: "Sides" },
    { value: "hat", label: "Hat" },
    { value: "hijab", label: "Hijab" },
    { value: "turban", label: "Turban" },
    { value: "winterHat1", label: "Winter Hat 1" },
    { value: "winterHat02", label: "Winter Hat 2" },
    { value: "winterHat03", label: "Winter Hat 3" },
    { value: "winterHat04", label: "Winter Hat 4" },
  ],
  // Hair color - hex values
  hairColor: [
    { value: "a55728", label: "Auburn" },
    { value: "2c1b18", label: "Black" },
    { value: "b58143", label: "Blonde" },
    { value: "d6b370", label: "Golden Blonde" },
    { value: "724133", label: "Brown" },
    { value: "4a312c", label: "Dark Brown" },
    { value: "f59797", label: "Pastel Pink" },
    { value: "ecdcbf", label: "Platinum" },
    { value: "c93305", label: "Red" },
    { value: "e8e1e1", label: "Silver Gray" },
  ],
  // Accessories - v7.x values
  accessories: [
    { value: "", label: "None" },
    { value: "kurt", label: "Kurt Glasses" },
    { value: "prescription01", label: "Prescription 1" },
    { value: "prescription02", label: "Prescription 2" },
    { value: "round", label: "Round Glasses" },
    { value: "sunglasses", label: "Sunglasses" },
    { value: "wayfarers", label: "Wayfarers" },
  ],
  // Facial hair - v7.x values
  facialHair: [
    { value: "", label: "None" },
    { value: "beardLight", label: "Light Beard" },
    { value: "beardMedium", label: "Medium Beard" },
    { value: "beardMajestic", label: "Majestic Beard" },
    { value: "moustacheFancy", label: "Fancy Moustache" },
    { value: "moustacheMagnum", label: "Magnum Moustache" },
  ],
  // Clothes - v7.x values
  clothing: [
    { value: "blazerAndShirt", label: "Blazer & Shirt" },
    { value: "blazerAndSweater", label: "Blazer & Sweater" },
    { value: "collarAndSweater", label: "Collar Sweater" },
    { value: "graphicShirt", label: "Graphic Shirt" },
    { value: "hoodie", label: "Hoodie" },
    { value: "overall", label: "Overall" },
    { value: "shirtCrewNeck", label: "Crew Neck" },
    { value: "shirtScoopNeck", label: "Scoop Neck" },
    { value: "shirtVNeck", label: "V-Neck" },
  ],
  // Clothes color - hex values
  clothesColor: [
    { value: "262e33", label: "Black" },
    { value: "65c9ff", label: "Blue" },
    { value: "5199e4", label: "Navy Blue" },
    { value: "25557c", label: "Dark Blue" },
    { value: "929598", label: "Gray" },
    { value: "3c4f5c", label: "Dark Gray" },
    { value: "b1e2ff", label: "Pastel Blue" },
    { value: "a7ffc4", label: "Pastel Green" },
    { value: "ffdeb5", label: "Pastel Orange" },
    { value: "ffafb9", label: "Pastel Red" },
    { value: "ffffb1", label: "Pastel Yellow" },
    { value: "ff488e", label: "Pink" },
    { value: "ff5c5c", label: "Red" },
    { value: "ffffff", label: "White" },
  ],
  // Skin tone - hex values
  skinColor: [
    { value: "fd9841", label: "Tanned" },
    { value: "f8d25c", label: "Yellow" },
    { value: "ffdbb4", label: "Pale" },
    { value: "edb98a", label: "Light" },
    { value: "d08b5b", label: "Brown" },
    { value: "ae5d29", label: "Dark Brown" },
    { value: "614335", label: "Black" },
  ],
  // Eyes - v7.x values
  eyes: [
    { value: "default", label: "Default" },
    { value: "happy", label: "Happy" },
    { value: "closed", label: "Closed" },
    { value: "cry", label: "Crying" },
    { value: "dizzy", label: "Dizzy" },
    { value: "eyeRoll", label: "Eye Roll" },
    { value: "hearts", label: "Hearts" },
    { value: "side", label: "Side" },
    { value: "squint", label: "Squint" },
    { value: "surprised", label: "Surprised" },
    { value: "wink", label: "Wink" },
    { value: "winkWacky", label: "Wacky Wink" },
  ],
  // Eyebrows - v7.x values
  eyebrows: [
    { value: "defaultNatural", label: "Default" },
    { value: "flatNatural", label: "Flat" },
    { value: "angryNatural", label: "Angry" },
    { value: "frownNatural", label: "Frown" },
    { value: "raisedExcitedNatural", label: "Excited" },
    { value: "sadConcernedNatural", label: "Sad" },
    { value: "unibrowNatural", label: "Unibrow" },
    { value: "upDownNatural", label: "Up Down" },
  ],
  // Mouth - v7.x values
  mouth: [
    { value: "default", label: "Default" },
    { value: "concerned", label: "Concerned" },
    { value: "disbelief", label: "Disbelief" },
    { value: "eating", label: "Eating" },
    { value: "grimace", label: "Grimace" },
    { value: "sad", label: "Sad" },
    { value: "screamOpen", label: "Scream" },
    { value: "serious", label: "Serious" },
    { value: "smile", label: "Smile" },
    { value: "tongue", label: "Tongue" },
    { value: "twinkle", label: "Twinkle" },
    { value: "vomit", label: "Sick" },
  ],
  // Background color - hex values
  backgroundColor: [
    { value: "b6e3f4", label: "Light Blue" },
    { value: "c0aede", label: "Lavender" },
    { value: "d1d4f9", label: "Periwinkle" },
    { value: "ffd5dc", label: "Pink" },
    { value: "ffdfbf", label: "Peach" },
    { value: "f4d9b2", label: "Tan" },
    { value: "d5f5e3", label: "Mint" },
    { value: "fdebd0", label: "Cream" },
    { value: "e8daef", label: "Light Purple" },
    { value: "d6eaf8", label: "Sky Blue" },
    { value: "fadbd8", label: "Blush" },
    { value: "abebc6", label: "Light Green" },
  ],
};

const DEFAULT_AVATAR = {
  top: "shortFlat",
  hairColor: "724133",
  accessories: "",
  facialHair: "",
  clothing: "blazerAndShirt",
  clothesColor: "65c9ff",
  skinColor: "edb98a",
  eyes: "default",
  eyebrows: "defaultNatural",
  mouth: "smile",
  backgroundColor: "b6e3f4",
};

const AvatarCustomizer = ({ 
  isOpen, 
  onClose, 
  currentAvatar, 
  twinName, 
  onSave,
  loading = false 
}) => {
  const [avatarConfig, setAvatarConfig] = useState(DEFAULT_AVATAR);
  const [activeCategory, setActiveCategory] = useState("face");
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (currentAvatar && typeof currentAvatar === 'object' && currentAvatar.top) {
      setAvatarConfig({ ...DEFAULT_AVATAR, ...currentAvatar });
    } else {
      // Generate random avatar
      setAvatarConfig({
        ...DEFAULT_AVATAR,
        top: AVATAR_OPTIONS.top[Math.floor(Math.random() * 10)].value,
        hairColor: AVATAR_OPTIONS.hairColor[Math.floor(Math.random() * AVATAR_OPTIONS.hairColor.length)].value,
        clothing: AVATAR_OPTIONS.clothing[Math.floor(Math.random() * AVATAR_OPTIONS.clothing.length)].value,
        clothesColor: AVATAR_OPTIONS.clothesColor[Math.floor(Math.random() * AVATAR_OPTIONS.clothesColor.length)].value,
      });
    }
  }, [currentAvatar, isOpen]);

  const generateAvatarUrl = (config) => {
    const params = new URLSearchParams();
    params.append("seed", twinName || "CareerTwin");
    
    if (config.top) params.append("top", config.top);
    if (config.hairColor) params.append("hairColor", config.hairColor);
    if (config.accessories) params.append("accessories", config.accessories);
    if (config.facialHair) params.append("facialHair", config.facialHair);
    if (config.clothing) params.append("clothing", config.clothing);
    if (config.clothesColor) params.append("clothesColor", config.clothesColor);
    if (config.skinColor) params.append("skinColor", config.skinColor);
    if (config.eyes) params.append("eyes", config.eyes);
    if (config.eyebrows) params.append("eyebrows", config.eyebrows);
    if (config.mouth) params.append("mouth", config.mouth);
    if (config.backgroundColor) params.append("backgroundColor", config.backgroundColor);
    
    return `https://api.dicebear.com/7.x/avataaars/svg?${params.toString()}`;
  };

  const handleOptionChange = (category, value) => {
    setAvatarConfig(prev => ({ ...prev, [category]: value }));
    setPreviewKey(prev => prev + 1);
  };

  const handleRandomize = () => {
    const randomConfig = {
      top: AVATAR_OPTIONS.top[Math.floor(Math.random() * AVATAR_OPTIONS.top.length)].value,
      hairColor: AVATAR_OPTIONS.hairColor[Math.floor(Math.random() * AVATAR_OPTIONS.hairColor.length)].value,
      accessories: AVATAR_OPTIONS.accessories[Math.floor(Math.random() * AVATAR_OPTIONS.accessories.length)].value,
      facialHair: AVATAR_OPTIONS.facialHair[Math.floor(Math.random() * AVATAR_OPTIONS.facialHair.length)].value,
      clothing: AVATAR_OPTIONS.clothing[Math.floor(Math.random() * AVATAR_OPTIONS.clothing.length)].value,
      clothesColor: AVATAR_OPTIONS.clothesColor[Math.floor(Math.random() * AVATAR_OPTIONS.clothesColor.length)].value,
      skinColor: AVATAR_OPTIONS.skinColor[Math.floor(Math.random() * AVATAR_OPTIONS.skinColor.length)].value,
      eyes: AVATAR_OPTIONS.eyes[Math.floor(Math.random() * AVATAR_OPTIONS.eyes.length)].value,
      eyebrows: AVATAR_OPTIONS.eyebrows[Math.floor(Math.random() * AVATAR_OPTIONS.eyebrows.length)].value,
      mouth: AVATAR_OPTIONS.mouth[Math.floor(Math.random() * AVATAR_OPTIONS.mouth.length)].value,
      backgroundColor: AVATAR_OPTIONS.backgroundColor[Math.floor(Math.random() * AVATAR_OPTIONS.backgroundColor.length)].value,
    };
    setAvatarConfig(randomConfig);
    setPreviewKey(prev => prev + 1);
    toast.success("Random avatar generated!");
  };

  const handleReset = () => {
    setAvatarConfig(DEFAULT_AVATAR);
    setPreviewKey(prev => prev + 1);
    toast.info("Avatar reset to default");
  };

  const handleSave = () => {
    onSave(avatarConfig);
  };

  const categories = [
    { id: "face", label: "Face", icon: <Smile className="w-4 h-4" /> },
    { id: "hair", label: "Hair", icon: <User className="w-4 h-4" /> },
    { id: "outfit", label: "Outfit", icon: <Shirt className="w-4 h-4" /> },
    { id: "accessories", label: "Extras", icon: <Glasses className="w-4 h-4" /> },
    { id: "background", label: "Background", icon: <Palette className="w-4 h-4" /> },
  ];

  const renderCategoryOptions = () => {
    switch (activeCategory) {
      case "face":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Skin Tone</Label>
              <div className="grid grid-cols-4 gap-2">
                {AVATAR_OPTIONS.skinColor.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleOptionChange("skinColor", option.value)}
                    className={`p-2 rounded-lg border-2 text-xs transition-all ${
                      avatarConfig.skinColor === option.value 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div 
                      className="w-6 h-6 rounded-full mx-auto mb-1" 
                      style={{ backgroundColor: `#${option.value}` }}
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Eyes</Label>
              <Select value={avatarConfig.eyes} onValueChange={(v) => handleOptionChange("eyes", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVATAR_OPTIONS.eyes.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Eyebrows</Label>
              <Select value={avatarConfig.eyebrows} onValueChange={(v) => handleOptionChange("eyebrows", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVATAR_OPTIONS.eyebrows.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Mouth</Label>
              <Select value={avatarConfig.mouth} onValueChange={(v) => handleOptionChange("mouth", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVATAR_OPTIONS.mouth.map(option => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case "hair":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Hair Style</Label>
              <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                {AVATAR_OPTIONS.top.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleOptionChange("top", option.value)}
                    className={`p-2 rounded-lg border-2 text-xs transition-all ${
                      avatarConfig.top === option.value 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Hair Color</Label>
              <div className="grid grid-cols-5 gap-2">
                {AVATAR_OPTIONS.hairColor.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleOptionChange("hairColor", option.value)}
                    className={`p-2 rounded-lg border-2 text-xs transition-all ${
                      avatarConfig.hairColor === option.value 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div 
                      className="w-6 h-6 rounded-full mx-auto mb-1" 
                      style={{ backgroundColor: `#${option.value}` }}
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Facial Hair</Label>
              <Select value={avatarConfig.facialHair || "none"} onValueChange={(v) => handleOptionChange("facialHair", v === "none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVATAR_OPTIONS.facialHair.map(option => (
                    <SelectItem key={option.value || "none"} value={option.value || "none"}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      case "outfit":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Clothing Style</Label>
              <div className="grid grid-cols-3 gap-2">
                {AVATAR_OPTIONS.clothing.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleOptionChange("clothing", option.value)}
                    className={`p-2 rounded-lg border-2 text-xs transition-all ${
                      avatarConfig.clothing === option.value 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Clothing Color</Label>
              <div className="grid grid-cols-5 gap-2">
                {AVATAR_OPTIONS.clothesColor.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleOptionChange("clothesColor", option.value)}
                    className={`p-2 rounded-lg border-2 text-xs transition-all ${
                      avatarConfig.clothesColor === option.value 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div 
                      className="w-6 h-6 rounded-full mx-auto mb-1" 
                      style={{ backgroundColor: `#${option.value}` }}
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case "accessories":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Eyewear</Label>
              <div className="grid grid-cols-3 gap-2">
                {AVATAR_OPTIONS.accessories.map(option => (
                  <button
                    key={option.value || "none"}
                    onClick={() => handleOptionChange("accessories", option.value)}
                    className={`p-2 rounded-lg border-2 text-xs transition-all ${
                      avatarConfig.accessories === option.value 
                        ? "border-indigo-500 bg-indigo-50" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      case "background":
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Background Color</Label>
              <div className="grid grid-cols-4 gap-3">
                {AVATAR_OPTIONS.backgroundColor.map(option => (
                  <button
                    key={option.value}
                    onClick={() => handleOptionChange("backgroundColor", option.value)}
                    className={`aspect-square rounded-xl border-3 transition-all relative ${
                      avatarConfig.backgroundColor === option.value 
                        ? "border-indigo-500 ring-2 ring-indigo-300" 
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    style={{ backgroundColor: `#${option.value}` }}
                    title={option.label}
                  >
                    {avatarConfig.backgroundColor === option.value && (
                      <Check className="w-4 h-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-700" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden" data-testid="avatar-customizer-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-5 h-5 text-indigo-500" />
            Customize Your Career Twin
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          {/* Preview Section */}
          <div className="flex flex-col items-center">
            <Card className="w-full bg-gradient-to-br from-indigo-50 to-purple-50">
              <CardContent className="p-6 flex flex-col items-center">
                <div 
                  className="w-48 h-48 rounded-full overflow-hidden shadow-xl border-4 border-white mb-4"
                  style={{ backgroundColor: `#${avatarConfig.backgroundColor}` }}
                >
                  <img 
                    key={previewKey}
                    src={generateAvatarUrl(avatarConfig)}
                    alt="Avatar Preview"
                    className="w-full h-full"
                    data-testid="avatar-preview"
                  />
                </div>
                <h3 className="text-lg font-bold text-gray-800">{twinName}</h3>
                <Badge variant="secondary" className="mt-1">Your Career Twin</Badge>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRandomize}
                    className="gap-1"
                    data-testid="randomize-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    Random
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleReset}
                    className="gap-1"
                    data-testid="reset-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Customization Options */}
          <div className="flex flex-col">
            {/* Category Tabs */}
            <div className="flex gap-1 mb-4 overflow-x-auto pb-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    activeCategory === cat.id
                      ? "bg-indigo-100 text-indigo-700"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.icon}
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Options Panel */}
            <div className="flex-1 overflow-y-auto max-h-[350px] pr-2">
              {renderCategoryOptions()}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="save-avatar-btn"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Avatar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AvatarCustomizer;
